/**
 * Field: label, control, help, error.
 *
 * The frame is exported on its own because Select and the type to confirm
 * input mount inside the same anatomy. One frame means the help line and the
 * error line sit in the same place on every form in the product, which is what
 * lets a person stop looking for them.
 *
 * Help and error are mutually exclusive by construction. A control that shows
 * both at once is asking the reader to work out which one is current.
 */

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import '../../styles/components/field.css';

export interface FieldFrameProps {
  label: string;
  /** Id of the control this label points at. */
  controlId: string;
  help?: string;
  error?: string;
  disabled?: boolean;
  /** Rendered to the right of the label. Used for optional markers. */
  aside?: ReactNode;
  children: ReactNode;
}

/** Deterministic ids for the describedby wiring, computed once per field. */
export function describedBy(controlId: string, help?: string, error?: string): string | undefined {
  const ids: string[] = [];
  if (error) ids.push(`${controlId}-error`);
  else if (help) ids.push(`${controlId}-help`);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

export function FieldFrame({
  label,
  controlId,
  help,
  error,
  disabled,
  aside,
  children,
}: FieldFrameProps) {
  return (
    <div className="field" data-disabled={disabled === true ? 'yes' : 'no'}>
      <div className="field__head">
        <label className="field__label" htmlFor={controlId}>
          {label}
        </label>
        {aside ? <span className="field__aside">{aside}</span> : null}
      </div>
      {children}
      {error ? (
        <p className="field__error" id={`${controlId}-error`}>
          {error}
        </p>
      ) : help ? (
        <p className="field__help" id={`${controlId}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  help?: string;
  error?: string;
  /** Mono means copyable literal and nothing else: identifiers, keys, names. */
  mono?: boolean;
  id?: string;
  /**
   * A control that sits inside the input's own box, at its trailing edge.
   *
   * One caller today, the show/hide toggle on a secret value, and it lives
   * here rather than in that screen because the input has to reserve the
   * space for it: an adornment overlaid on a field that does not know about
   * it sits on top of whatever the person typed.
   */
  adornment?: ReactNode;
}

export function Field({
  label,
  help,
  error,
  mono,
  id,
  disabled,
  adornment,
  className,
  ...rest
}: FieldProps) {
  const generated = useId();
  const controlId = id ?? generated;

  const input = (
    <input
      {...rest}
      id={controlId}
      className={className ? `field__input ${className}` : 'field__input'}
      data-mono={mono === true ? 'yes' : 'no'}
      data-adorned={adornment ? 'yes' : 'no'}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(controlId, help, error)}
    />
  );

  return (
    <FieldFrame label={label} controlId={controlId} help={help} error={error} disabled={disabled}>
      {adornment ? (
        <div className="field__wrap">
          {input}
          <span className="field__adornment">{adornment}</span>
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  );
}
